/**
 * Life / life-expectancy panel (§A.7). Reads the OBS-7 `life` ref — a
 * `{projection: {...}}` document (`src/types/incremental.ts`'s `LifeDocument`).
 * Degrades to "not published yet" when the ref is `null`/unreadable.
 *
 * design/reshape/RESHAPE.md §3/§4: plain-language labels first, the technical
 * term secondary, shares as percentages.
 *
 * OBS2 (2026-10-03):
 * - It follows new publishes on the shared poll cadence (it rendered once per
 *   visit), re-rendering only when the content-addressed document changes.
 * - It leads with the real distance to economic death (W(t) vs the solvency
 *   floor) and the lifecycle stage with its binding progress.
 * - The brain's survival head is shown as the labelled estimate it is, with its
 *   measured calibration — never as a death clock (it projected "1 cycle left"
 *   while both flies sat ~30% above their floor). A projection that resolves no
 *   death within its horizon reads "> horizon", not a forecast of the horizon.
 */

import type { RoleSnapshot } from "../data/snapshotIndex";
import { renderNotPublishedYet } from "./NotPublishedYet";
import { attachTooltip } from "../ui/tooltip";
import { isLifeSubstantive } from "./lifeSubstantive";
import { statLabel } from "../ui/statLabel";
import { formatCount, formatDecimal, formatPercent } from "../format/number";
import { computeLifeBandLayout } from "./lifeBand";
import type { LifeDocument, LifeProjection } from "../types/incremental";
import { Poller, freshnessBadgeLabel } from "../data/polling";
import { isNotPublishedYet } from "../data/fetchJson";
import { config } from "../config";
import {
  cyclesText,
  bindingRows,
  capitalAlertRows,
  economicDeathRows,
  isCensored,
  lifeFingerprint,
  survivalHeadRows,
  timeLeftText,
  type LifeRow,
} from "./lifeView";

export class LifePanel {
  /** Set once `mount()` resolves — `OrganismScreen` reads this (DESIGN.md
   * §7's second empty state) to decide whether the Life panel should start
   * as a slim collapsed strip. */
  substantive = false;

  private poller: Poller<LifeDocument | null> | null = null;
  private freshnessEl: HTMLSpanElement | null = null;
  private disposed = false;
  /** The rendered document's fingerprint: the poller's first tick re-fetches the
   * document `mount()` just drew, which must not rebuild the panel. */
  private renderedKey: string | null = null;

  constructor(
    private readonly container: HTMLElement,
    private readonly role: RoleSnapshot,
  ) {}

  async mount(): Promise<void> {
    this.container.innerHTML = "";
    const initial = await this.safeFetch();
    if (this.disposed) return;
    this.render(initial);
    this.poller = new Poller<LifeDocument | null>(
      async () => {
        const doc = await this.safeFetch();
        return { key: lifeFingerprint(doc), value: doc };
      },
      (snapshot) => {
        // Only a real change re-renders; a transient error keeps what is on screen.
        if (
          (snapshot.status === "ok" || snapshot.status === "not-published-yet") &&
          lifeFingerprint(snapshot.value) !== this.renderedKey
        ) {
          this.render(snapshot.value);
        }
        if (this.freshnessEl) {
          this.freshnessEl.textContent = freshnessBadgeLabel(
            snapshot.status,
            snapshot.lastSuccessAt,
          );
        }
      },
      { intervalMs: config.pollIntervalMs, backoffMaxMs: config.pollBackoffMaxMs },
      isNotPublishedYet,
    );
    this.poller.start();
  }

  private async safeFetch(): Promise<LifeDocument | null> {
    try {
      return await this.role.life();
    } catch (error) {
      if (isNotPublishedYet(error)) return null;
      throw error;
    }
  }

  private render(life: LifeDocument | null): void {
    if (this.disposed) return;
    this.container.innerHTML = "";
    this.renderedKey = lifeFingerprint(life);
    this.substantive = isLifeSubstantive(life);
    if (!life) {
      this.container.appendChild(
        renderNotPublishedYet(
          "Life expectancy",
          "OBS-7's life-expectancy model has not been published for this organism yet.",
        ),
      );
      return;
    }
    const header = document.createElement("div");
    header.className = "obs-sizing-header";
    const caption = document.createElement("span");
    caption.textContent = `stage ${life.stage ?? "–"} · ${life.sleep_state ?? "–"}`;
    header.appendChild(caption);
    const freshness = document.createElement("span");
    freshness.className = "obs-freshness";
    freshness.dataset["testid"] = "life-freshness";
    // Keep the last poll's label across a re-render (the poller sets it after render).
    freshness.textContent = this.freshnessEl?.textContent ?? "";
    this.freshnessEl = freshness;
    header.appendChild(freshness);
    this.container.appendChild(header);

    const projection = life.projection;
    this.container.appendChild(
      this.section("Economic death", economicDeathRows(life.economic_death)),
    );
    const alertRows = capitalAlertRows(life.capital_alert);
    if (alertRows.length > 0) this.container.appendChild(this.section("Capital alert", alertRows));
    this.container.appendChild(this.section("Life stage", bindingRows(life.stage, life.binding)));

    const projectionSection = this.section("Projected life", this.projectionRows(projection));
    projectionSection.insertBefore(
      this.renderBand(projection),
      projectionSection.children[1] ?? null,
    );
    if (!this.substantive) {
      const hint = document.createElement("div");
      hint.className = "obs-panel-hint";
      hint.textContent = `${projection.model} model — no cycle projection published yet for this organism.`;
      projectionSection.appendChild(hint);
    }
    this.container.appendChild(projectionSection);

    const head = survivalHeadRows(life.survival_head);
    if (head.length > 0) this.container.appendChild(this.section("Brain estimate", head));
  }

  private projectionRows(projection: LifeProjection): LifeRow[] {
    const rows: LifeRow[] = [
      {
        plainLabel: "Model",
        tooltip:
          "Which life-expectancy model produced this projection. v2 projects economic death " +
          "(Monte-Carlo W(t) paths against the solvency floor) and confirmed regime dwell; the " +
          "brain's survival head is not a death hazard and is not used.",
        value: projection.model,
      },
      {
        plainLabel: "Typical time left",
        technicalLabel: "median, decision cycles",
        tooltip:
          "P50 remaining decision cycles to the earlier of natural or economic death. When most " +
          "simulated paths never reach either within the horizon, this reads '> horizon'.",
        value: timeLeftText(projection),
      },
    ];
    // Review: always shown — with 40% of paths dying inside the horizon P10 is the
    // most informative number even though the median is censored ("> horizon").
    rows.push(
      {
        plainLabel: "Pessimistic",
        technicalLabel: "P10, cycles",
        tooltip:
          "10th percentile (pessimistic) remaining cycles; '> horizon' when even it resolves " +
          "no death within the horizon.",
        value: cyclesText(projection.p10_cycles, projection),
      },
      {
        plainLabel: "Optimistic",
        technicalLabel: "P90, cycles",
        tooltip: "90th percentile (optimistic) remaining cycles.",
        value: cyclesText(projection.p90_cycles, projection),
      },
    );
    rows.push(
      {
        plainLabel: "Main risk",
        technicalLabel: "dominant hazard",
        tooltip:
          "Whichever of natural (regime exit) or economic (solvency) hazard currently dominates; " +
          "'unknown' when neither resolves within the horizon.",
        value: projection.dominant_hazard,
      },
      {
        plainLabel: "Share from money risk",
        technicalLabel: "economic",
        tooltip: "Share of simulated paths that end in economic death first within the horizon.",
        value: formatPercent(projection.economic_share),
      },
      {
        plainLabel: "Share from market-regime risk",
        technicalLabel: "natural",
        tooltip:
          "Share of simulated paths that end in natural (regime-exit) death first within the " +
          "horizon. 'Prior' until a regime episode has been confirmed over.",
        value: formatPercent(projection.natural_share),
      },
      {
        plainLabel: "Unresolved in horizon",
        technicalLabel: "censored",
        tooltip: "Share of Monte-Carlo draws that never crossed either hazard within the horizon.",
        value: formatPercent(projection.censored_share),
      },
    );
    return rows;
  }

  private section(title: string, rows: readonly LifeRow[]): HTMLDivElement {
    const section = document.createElement("div");
    section.className = "obs-life-section";
    const heading = document.createElement("div");
    heading.className = "obs-life-section-title";
    heading.textContent = title;
    section.appendChild(heading);
    if (rows.length === 0) {
      const empty = document.createElement("div");
      empty.className = "obs-panel-hint";
      empty.textContent = "Not published for this organism yet.";
      section.appendChild(empty);
      return section;
    }
    for (const row of rows) {
      const rowEl = document.createElement("div");
      rowEl.className = "obs-stat-row";
      const labelEl = statLabel(row.plainLabel, row.technicalLabel);
      attachTooltip(labelEl, row.tooltip);
      const valueEl = document.createElement("span");
      valueEl.textContent = row.value;
      rowEl.appendChild(labelEl);
      rowEl.appendChild(valueEl);
      section.appendChild(rowEl);
    }
    return section;
  }

  /** The P10/median/P90 band chart (DIRECTION.md §1.6). Never drawn from an
   * incomplete projection, nor from a censored one (a full-width band at the
   * horizon would read as a forecast of the horizon itself). */
  private renderBand(p: LifeProjection): HTMLDivElement {
    const wrap = document.createElement("div");
    wrap.className = "obs-life-band";
    const layout = isCensored(p)
      ? null
      : computeLifeBandLayout(p.p10_cycles, p.median_cycles, p.p90_cycles);

    if (!layout) {
      const empty = document.createElement("div");
      empty.className = "obs-life-band-empty";
      const horizon = p.inputs["horizon_cycles"];
      const horizonText = typeof horizon === "number" ? `${formatCount(horizon)}-cycle ` : "";
      empty.textContent = isCensored(p)
        ? `No modeled death within the ${horizonText}horizon.`
        : "Not enough of a projection to draw a range yet — see the table below.";
      wrap.appendChild(empty);
      return wrap;
    }

    const track = document.createElement("div");
    track.className = "obs-life-band-track";
    const fill = document.createElement("div");
    fill.className = "obs-life-band-fill";
    fill.style.left = `${layout.p10Pct}%`;
    fill.style.width = `${Math.max(0, layout.p90Pct - layout.p10Pct)}%`;
    const median = document.createElement("div");
    median.className = "obs-life-band-median";
    median.style.left = `${layout.medianPct}%`;
    track.appendChild(fill);
    track.appendChild(median);
    attachTooltip(
      track,
      `Model: ${p.model}. Band spans P10-P90 remaining decision cycles; the tick marks the median — ` +
        "the same figures as the table below, drawn to scale.",
    );
    wrap.appendChild(track);

    const labels = document.createElement("div");
    labels.className = "obs-life-band-labels";
    const left = document.createElement("span");
    left.textContent = `P10 ${formatDecimal(p.p10_cycles)}`;
    const mid = document.createElement("span");
    mid.textContent = `Median ${formatDecimal(p.median_cycles)}`;
    const right = document.createElement("span");
    right.textContent = `P90 ${formatDecimal(p.p90_cycles)}`;
    labels.appendChild(left);
    labels.appendChild(mid);
    labels.appendChild(right);
    wrap.appendChild(labels);
    return wrap;
  }

  dispose(): void {
    this.disposed = true;
    this.poller?.stop();
  }
}
